import { useState, useEffect, useRef } from 'react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import SuperadminLayout from '../../components/SuperadminLayout';
import SearchSelect from '../../components/SearchSelect';
import { HardDriveDownload, Download, Loader2, CircleCheck, Check } from 'lucide-react';

// Superadmin → Baza Backup: tenant seç, onun DB + şəkillərini tək .zip endir.
const mb = (b) => (Number(b || 0) / 1024 / 1024).toFixed(1);

export default function Backup() {
    const [tenants, setTenants]   = useState([]);
    const [tenantId, setTenantId] = useState('');
    const [phase, setPhase]       = useState('idle');  // idle|prep|downloading|done
    const [prepPct, setPrepPct]   = useState(0);       // server hazırlıq (təxmini)
    const [dl, setDl]             = useState({ pct: 0, loaded: 0, total: 0 });
    const creepRef = useRef(null);

    useEffect(() => {
        api.get('/superadmin/tenants').then(r => setTenants(r.data.data || [])).catch(() => {});
    }, []);

    // Server-tərəf hazırlıq üçün hərəkət edən zolaq (95%-ə qədər sürünür)
    const startCreep = () => {
        stopCreep(); let v = 0; setPrepPct(0);
        creepRef.current = setInterval(() => {
            v = Math.min(95, v + Math.max(0.4, (95 - v) * 0.05));
            setPrepPct(Math.round(v));
        }, 400);
    };
    const stopCreep = () => { if (creepRef.current) { clearInterval(creepRef.current); creepRef.current = null; } };
    useEffect(() => () => stopCreep(), []);

    const busy = phase === 'prep' || phase === 'downloading';

    const download = async () => {
        if (!tenantId) { toast.error('Tenant seçin.'); return; }
        setPhase('prep'); setDl({ pct: 0, loaded: 0, total: 0 }); startCreep();
        try {
            const res = await api.get(`/superadmin/tenant-backup/${tenantId}`, {
                responseType: 'blob',
                timeout: 600000,
                onDownloadProgress: (e) => {
                    // İlk bayt gələndə hazırlıq bitdi → real endirmə %-i
                    stopCreep();
                    setPhase('downloading');
                    const total = e.total || 0;
                    setDl({ pct: total ? Math.round((e.loaded / total) * 100) : 0, loaded: e.loaded, total });
                },
            });
            stopCreep();
            let name = `tenant_${tenantId}_backup.zip`;
            const cd = res.headers['content-disposition'] || '';
            const m = cd.match(/filename="?([^"]+)"?/);
            if (m) name = m[1];

            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
            const a = document.createElement('a');
            a.href = url; a.download = name;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            setPhase('done');
            toast.success('Backup endirildi ✓');
        } catch (err) {
            stopCreep();
            setPhase('idle');
            toast.error('Backup alınmadı: ' + (err.response?.data?.msg || err.message));
        }
    };

    const opts = tenants.map(t => ({ value: t.id, label: `${t.name || t.username} (#${t.id})` }));

    return (
        <SuperadminLayout title="Baza backup" subtitle="Bir tenantın bütün məlumatını (DB + şəkillər) tək .zip faylı kimi endir">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
                <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)] lg:col-span-3">
                    <div className="mb-5 flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50 text-[#0090bb]">
                            <HardDriveDownload className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="text-sm font-semibold text-slate-900">Tenant backup-ı</div>
                            <div className="text-xs text-slate-500">DB + şəkillər tək .zip faylında</div>
                        </div>
                    </div>

                    <label className="mb-1.5 block text-xs font-medium text-slate-600">Restoran</label>
                    <SearchSelect value={tenantId} onChange={setTenantId} options={opts}
                        placeholder="Restoran axtar və ya seç" />

                    <button onClick={download} disabled={busy || !tenantId}
                        className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#0090bb] px-4 py-3 text-sm font-semibold text-white shadow-sm shadow-sky-900/20 transition-colors hover:bg-[#007ea4] disabled:opacity-50">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        {busy ? 'Hazırlanır…' : 'Backup endir (.zip)'}
                    </button>

                    {/* Progress */}
                    {(phase === 'prep' || phase === 'downloading') && (
                        <div className="mt-5">
                            <div className="mb-1.5 flex justify-between text-xs font-medium text-slate-500">
                                {phase === 'prep' ? (
                                    <><span>Server hazırlayır (DB + şəkillər arxivlənir)…</span><span className="font-mono">{prepPct}%</span></>
                                ) : (
                                    <><span>Endirilir… {mb(dl.loaded)}{dl.total ? ` / ${mb(dl.total)} MB` : ' MB'}</span><span className="font-mono">{dl.pct}%</span></>
                                )}
                            </div>
                            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                                <div className={`h-full rounded-full transition-all ${phase === 'prep' ? 'bg-amber-400' : 'bg-[#0090bb]'}`}
                                    style={{ width: `${phase === 'prep' ? prepPct : dl.pct}%` }} />
                            </div>
                        </div>
                    )}
                    {phase === 'done' && (
                        <p className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                            <CircleCheck className="h-4 w-4" /> Endirildi.
                        </p>
                    )}
                </div>

                <div className="rounded-2xl border border-slate-200/80 bg-white p-6 text-sm text-slate-600 shadow-[0_1px_2px_rgba(15,23,42,0.04)] lg:col-span-2">
                    <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Nə daxildir</div>
                    <ul className="space-y-2.5">
                        {[
                            'Bütün DB məlumatı: menyu, sifarişlər, kassa, işçilər, anbar',
                            'Tenantın ayarları, flaglar, logo, QR tema',
                            'Bütün şəkillər: menyu, fon, logo, QR, kateqoriya',
                            'meta.json: hansı tenant olduğunu qeyd edir (restore üçün)',
                        ].map((x) => (
                            <li key={x} className="flex gap-2 text-[13px] leading-snug">
                                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />{x}
                            </li>
                        ))}
                    </ul>
                    <p className="mt-4 rounded-xl bg-slate-50 p-3 text-xs text-slate-500">Bu fayl <b className="text-slate-700">Baza restore</b> bölməsində geri yüklənə bilər.</p>
                </div>
            </div>
        </SuperadminLayout>
    );
}
